import {reactive, inject, ref, onUnmounted, type Ref, nextTick} from "vue";

import {logError} from "@songfinch/utils/src/error";

import type {
    SongTrackingDataType,
    InjectedAudioTrackEvent,
} from "@songfinch/types";


const audioPlayerState = reactive<{
  instances: Map<Ref<HTMLAudioElement | null>, Ref<boolean>>;
}>({
    instances: new Map(),
});

let intervalId: number | undefined;

export type SongMetaObjectType = {
    artist: string;
    genres: string;
    title: string;
};

export function useAudioPlayer(src: string, songMetaData: SongMetaObjectType) {
    const audioTrackEvent = inject<InjectedAudioTrackEvent | null>("audioTrackEvent", null);
    
    const audio = ref<HTMLAudioElement | null>(null);
    const isPlaying = ref(false);
    const currentTime = ref(0);

    const initAudio = async () => {
        if (!audio.value) {
            audio.value = new Audio(src);
            audio.value.preload = "auto";
            audio.value.addEventListener("timeupdate", () => {
                if (audio.value) currentTime.value = audio.value.currentTime;
            });
        }
    };

    const hasAudioTrackingEvent = (value: InjectedAudioTrackEvent | null): value is InjectedAudioTrackEvent  => {
        if (!value) {
            // TODO hanlde tracking for this commponent (ignore this for now cause its never been setup)
            // logError("audioTrackEvent is missing", {
            //     level: "warning",
            //     extra: {file: "useAudioPlayer.ts"},
            // });
            return false;
        }
        return true;
    };

    const handlePauseAudioTrackingEvent = (e:  MouseEvent | TouchEvent) => {
        if (hasAudioTrackingEvent(audioTrackEvent)) {
            let percentagePlayed;
            if (audio.value?.currentTime && audio.value?.duration) {
                percentagePlayed = Math.round(
                    (audio.value?.currentTime / audio.value?.duration) * 100,
                );
            }
    
            const trackingObject: SongTrackingDataType = {
                ...songMetaData,
                time_played: audio.value?.currentTime.toFixed(2) || undefined,
                audio_duration: audio.value?.duration.toFixed(2) || undefined,
                percentage_played: percentagePlayed ? `${percentagePlayed}%` : undefined,
            };
            audioTrackEvent(e, "audio_paused", trackingObject);
        }
    };
    
    const handleAudioPlayedTrackingEvent = (e:  MouseEvent | TouchEvent) => {
        if (hasAudioTrackingEvent(audioTrackEvent)) {
            audioTrackEvent(e, "audio_played", songMetaData);
        }
    };

    const play = async (e:  MouseEvent | TouchEvent, startTime?: number = 0, endTime?:  number) => {
        try {
            await initAudio();
            await nextTick();
            if (!audio.value) return;
            // Stop all other playing instances
            audioPlayerState.instances.forEach((playingRef, audioRef) => {
                if (audioRef.value && audioRef.value !== audio.value && playingRef.value) {
                    audioRef.value.pause();
                    audioRef.value.currentTime = 0;
                    playingRef.value = false;
                }
            });

            audio.value.currentTime = startTime;
            await nextTick();

            await audio.value.play();
            isPlaying.value = true;
            audioPlayerState.instances.set(audio, isPlaying);
            handleAudioPlayedTrackingEvent(e);

            if (endTime !== undefined) {
                if (intervalId !== undefined) {
                    clearIntervalAndId();
                }

                intervalId = setInterval(() => {
                    if (audio.value && audio.value.currentTime >= endTime) {
                        audio.value.pause();
                        isPlaying.value = false;
                    }
                }, 200);
            }
        } catch (error) {
            logError(error, {
                extra: {src, songMetaData}
            });
        }
    };

    const clearIntervalAndId = () => {
        if (intervalId !== undefined) {
            clearInterval(intervalId);
            intervalId = undefined;
        }
    };

    const pause = (e: MouseEvent | TouchEvent) => {
        if (audio.value && !audio.value.paused) {
            audio.value.pause();
            isPlaying.value = false;
            audioPlayerState.instances.delete(audio);
            handlePauseAudioTrackingEvent(e);
            clearIntervalAndId();
        }
    };

    const stop = () => {
        if (audio.value) {
            audio.value.pause();
            audio.value.currentTime = 0;
            isPlaying.value = false;
            audioPlayerState.instances.delete(audio);
            clearIntervalAndId();
        }
    };

    
    const playPauseAudio = (e: MouseEvent, startTime?: number, endTime?: endTime) => {
        if (isPlaying.value) {
            pause(e);}
        else {
            play(e, startTime, endTime);
        } 
    };
    

    onUnmounted(() => {
        if (audio.value) {
            audio.value.pause();
            audio.value.src = "";
            audio.value.load();
        }

        audioPlayerState.instances.delete(audio);
    });

    return {
        play, 
        pause, 
        playPauseAudio,
        stop, 
        isPlaying
    };
}
